/** Display helpers shared by the screens. Formatting only — no data decisions. */
import { now } from './clock';

const DAY_MS = 86_400_000;

/** 3.7M, 12.4K, 812 — a tile has room for about five characters. */
export function compact(value: number): string {
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

export function whole(value: number): string {
  return value.toLocaleString('en');
}

/**
 * The last `days` UTC days, oldest first, with zeros where nothing happened —
 * the API only returns days that had something, and a gap is information.
 */
export function dailySeries(
  points: readonly { day: string; count: number }[],
  days: number,
  now: Date,
): { key: string; count: number }[] {
  const byDay = new Map<string, number>();
  for (const p of points) byDay.set(p.day, (byDay.get(p.day) ?? 0) + p.count);

  const series: { key: string; count: number }[] = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const key = new Date(now.getTime() - i * DAY_MS).toISOString().slice(0, 10);
    series.push({ key, count: byDay.get(key) ?? 0 });
  }
  return series;
}

export function shortDay(isoDay: string | undefined): string {
  if (isoDay === undefined) return '';
  return new Date(`${isoDay}T00:00:00Z`).toLocaleDateString('en', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** "12 min ago", "3 h ago", "yesterday" — for lists read at a glance. */
export function ago(iso: string, at: Date = now()): string {
  const minutes = Math.round((at.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} d ago`;
}

/** "2026-09-18 14:22" — the table view's sortable stamp. */
export function stamp(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** What a record kind is called on screen: the enum is for the wire, not for people. */
const KIND_LABELS: Readonly<Record<string, string>> = {
  message: 'Messages',
  vehicle: 'Vehicles',
  driver: 'Drivers',
  trip: 'Trips',
  load: 'Loads',
  alert: 'Alerts',
  document: 'Documents',
  location: 'Locations',
  hos: 'Hours of service',
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind.charAt(0).toUpperCase() + kind.slice(1).replace(/_/g, ' ');
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "09:41" in the viewer's time zone. */
export function clockTime(iso: string): string {
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** "Sep 18" in the viewer's time zone. */
export function monthDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

/** "14d 06h" under a day it is "5h 07m", under an hour "12m". */
export function uptime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${pad2(hours % 24)}h`;
  if (hours > 0) return `${hours}h ${pad2(minutes % 60)}m`;
  return `${minutes}m`;
}

/** "+12% vs yesterday", or nothing to compare against when yesterday was zero. */
export function versusYesterday(today: number, yesterday: number): string {
  if (yesterday === 0) return today === 0 ? 'none yesterday either' : 'none yesterday';
  const change = Math.round(((today - yesterday) / yesterday) * 100);
  return `${change > 0 ? '+' : ''}${change}% vs yesterday`;
}

/** "210 ms", "1.2 s" — a latency that fits a narrow column; a dash when unmeasured. */
export function millis(ms: number | undefined): string {
  if (ms === undefined) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

/** "Anna Kowalski" → "AK": the avatar tile of the users table and the header menu. */
export function initials(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase())
    .filter((letter) => letter !== '');
  return letters.slice(0, 2).join('') || '?';
}

/** "admin" → "Admin". */
export function roleLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/**
 * A money-like amount with a thousands separator: "1,250", "1,250.75". The
 * records carry plain numbers, so nothing is rounded up to a fixed currency
 * precision and no symbol is invented.
 */
export function amount(value: number): string {
  return value.toLocaleString('en', { maximumFractionDigits: 2 });
}

/**
 * The same amount with the provider's own currency code in front, when a
 * documented field carried one — "USD 1,250". No symbol is ever guessed.
 */
export function amountIn(value: number, currency: string | undefined): string {
  return currency === undefined || currency === '' ? amount(value) : `${currency} ${amount(value)}`;
}

/** "Mon" for a `YYYY-MM-DD` day, read as UTC like the rest of the day helpers. */
export function weekday(isoDay: string): string {
  return new Date(`${isoDay}T00:00:00Z`).toLocaleDateString('en', {
    weekday: 'short',
    timeZone: 'UTC',
  });
}
